// v2.20.2 · suscripción a la cola RECIENTE del canal rápido por sesión/secuencia. Es el mismo
// contrato de v2.20.1 (ceros, huecos y tiempos reales se conservan): aquí sólo se reutiliza para
// la curva grande de la ficha y para las mini curvas del mosaico.
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

export default function useCurvaViva(camaId, { activo = true, intervaloMs = 1000, ventana = 256 } = {}) {
  const [vivo, setVivo] = useState(null);
  const [sinAcceso, setSinAcceso] = useState(false);
  const cursorRef = useRef({ session: null, seq: null });
  const enVueloRef = useRef(false);
  const vigenteRef = useRef(camaId);

  const cargar = useCallback(async () => {
    if (!camaId) return;
    // Cerca de concurrencia: con el ciclo anterior en vuelo no se dispara otro con el mismo
    // cursor (dos respuestas del mismo rango duplicarían muestras).
    if (enVueloRef.current) return;
    enVueloRef.current = true;
    try {
      const params = {};
      if (cursorRef.current.session) {
        params.session_id = cursorRef.current.session;
        if (cursorRef.current.seq != null) params.desde_seq = cursorRef.current.seq;
      }
      const { data } = await api.get(`/ekh/vista/camas/${camaId}/curva-viva`, { params });
      if (vigenteRef.current !== camaId) return;
      if (data?.cama_id && data.cama_id !== camaId) return;
      const h = data?.muestras?.heart_amplitude || [];
      const r = data?.muestras?.respiratory_amplitude || [];
      const ts = data?.muestras?.t || [];
      const reinicia = Boolean(data?.nueva_sesion) ||
        (data?.session_id && data.session_id !== cursorRef.current.session);
      cursorRef.current = {
        session: data?.session_id || cursorRef.current.session,
        seq: data?.ultimo_seq ?? cursorRef.current.seq,
      };
      setVivo((prev) => {
        const base = reinicia || !prev
          ? { heart: [], resp: [], t: [], huecos: [], recibidas: 0 } : prev;
        // Un hueco DECLARADO entre el tramo anterior y el nuevo se dibuja como corte (null).
        const corte = (!reinicia && base.heart.length && (data?.huecos || []).length) ? [null] : [];
        return {
          session_id: cursorRef.current.session,
          ultimo_seq: cursorRef.current.seq,
          cadencia_ms: data?.cadencia_ms ?? base.cadencia_ms ?? null,
          heart: [...base.heart, ...corte, ...h].slice(-ventana),
          resp: [...base.resp, ...corte, ...r].slice(-ventana),
          t: [...base.t, ...ts].slice(-ventana),
          huecos: [...(base.huecos || []), ...(data?.huecos || [])].slice(-20),
          recibidas: (base.recibidas || 0) + h.length,
          razon: data?.lectura?.razon || null,
        };
      });
    } catch (e) {
      const st = e?.response?.status;
      if ((st === 403 || st === 404) && vigenteRef.current === camaId) setSinAcceso(true);
    } finally {
      if (vigenteRef.current === camaId) enVueloRef.current = false;
    }
  }, [camaId, ventana]);

  useEffect(() => {
    vigenteRef.current = camaId;
    cursorRef.current = { session: null, seq: null };
    enVueloRef.current = false;
    setVivo(null);
    setSinAcceso(false);
  }, [camaId]);

  useEffect(() => {
    if (!activo || !camaId || sinAcceso) return;
    cargar();
    const t = setInterval(cargar, intervaloMs);
    return () => clearInterval(t);
  }, [activo, camaId, cargar, intervaloMs, sinAcceso]);

  return { vivo, sinAcceso };
}
